/**
 * AbilityProtectedRoute - Route protection using CASL
 *
 * Protects routes based on CASL abilities using backend permission values
 */

import { type FC, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AdminAction } from '@/app/models/AdminAction';
import { useAbility } from '../casl/AbilityContext';
import { type Resource } from '../casl/resources';
import { useAuth } from '../modules/auth/core/Auth';

interface AbilityProtectedRouteProps {
  children: ReactNode;
  /** Single permission requirement */
  permission?: Resource;
  /** Multiple permission requirements - user needs ANY of these */
  permissions?: Resource[];
  /** If true, user needs ALL permissions (default: false - needs ANY) */
  requireAll?: boolean;
  /** Path to redirect if unauthorized (default: /inicio/dashboard) */
  fallbackPath?: string;
}

/**
 * Route protection component using CASL abilities
 *
 * @example
 * // Single permission
 * <AbilityProtectedRoute permission={RESOURCES.CATALOGO_PRODUTOS}>
 *   <ProductPage />
 * </AbilityProtectedRoute>
 *
 * @example
 * // Multiple permissions (user needs ANY)
 * <AbilityProtectedRoute permissions={[
 *   RESOURCES.CATALOGO_PRODUTOS,
 *   RESOURCES.CATALOGO_CATEGORIAS,
 * ]}>
 *   <CatalogWrapper />
 * </AbilityProtectedRoute>
 */
export const AbilityProtectedRoute: FC<AbilityProtectedRouteProps> = ({
  children,
  permission,
  permissions = [],
  requireAll = false,
  fallbackPath = '/inicio/indice',
}) => {
  const ability = useAbility();
  const { currentUser } = useAuth();
  const location = useLocation();

  if (!currentUser) {
    return null;
  }

  // Combine single permission with permissions array
  const allPermissions = permission ? [permission, ...permissions] : permissions;

  // No permissions required = always accessible
  if (allPermissions.length === 0) {
    return children;
  }

  // Check permissions
  const hasAccess = requireAll
    ? allPermissions.every((p) => ability.can(AdminAction.CONSULTAR, p))
    : allPermissions.some((p) => ability.can(AdminAction.CONSULTAR, p));

  if (!hasAccess) {
    return <Navigate replace state={{ from: location.pathname }} to={fallbackPath} />;
  }

  return children;
};
